import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { fetchMerchantConfig, setMerchantEngine, setMerchantShadow } from '../../AllocationConfig/decisionConfigService';
import { C, RADIUS, SX, TYPE } from './tokens';
import ConfigSection from './ConfigSection';
import ConfigRow, { ConfigToggle } from './ConfigRow';
import { resolveAllocationStrategy } from './allocationStrategy';

const TONE = {
    ok:   { fg: C.ok,   bg: C.okSoft,   border: C.okBorder },
    info: { fg: C.info, bg: C.infoSoft, border: C.infoBorder },
    warn: { fg: C.warn, bg: C.warnSoft, border: C.warnBorder },
};

/**
 * States plainly which of the four allocation generations is actually routing
 * this merchant's orders, and what is configured but inert.
 *
 * This is the one thing that removes the most common category of config
 * confusion in the product: someone edits allocation rules on the Admin screen
 * for a merchant whose orders are being routed by PACE, and nothing tells
 * them the edit changed nothing.
 */
function StrategyBanner({ resolved }) {
    const tone = TONE[resolved.tone] || TONE.info;
    return (
        <Box sx={{
            display: 'flex', gap: 1.25, alignItems: 'flex-start',
            bgcolor: tone.bg, border: `1px solid ${tone.border}`,
            borderRadius: RADIUS.md, p: 1.75, mb: 1.5,
        }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: tone.fg, mt: '1px', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...TYPE.rowLabel, color: tone.fg }}>{resolved.label}</Typography>
                <Typography sx={{ ...TYPE.rowHelp, mt: 0.3, maxWidth: '70ch' }}>{resolved.detail}</Typography>

                {resolved.inert.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${tone.border}` }}>
                        <Typography sx={{ ...TYPE.eyebrow, color: tone.fg, mb: 0.5 }}>
                            Saved but not in use
                        </Typography>
                        {resolved.inert.map((i, n) => (
                            <Typography key={n} sx={{ ...TYPE.rowHelp, display: 'block' }}>
                                <b style={{ color: C.inkBody }}>{i.what}</b> — {i.why}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default function AllocationEnginePanel({ merchantId, merchant, children }) {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState(null);

    const load = useCallback(() => {
        if (!merchantId) return;
        setLoading(true);
        fetchMerchantConfig(merchantId)
            .then((res) => setCfg(res?.data || {}))
            .catch((err) => { console.error(err); setCfg({}); })
            .finally(() => setLoading(false));
    }, [merchantId]);

    useEffect(load, [load]);

    const resolved = resolveAllocationStrategy(cfg || {}, merchant || {});
    const onV2 = (cfg?.allocationEngineVersion || '').toUpperCase() === 'V2';

    // Both engine writes are ProshipAdmin-gated on the server; the confirm step
    // is here because switching the engine changes how every subsequent order is
    // routed, and that is not something to do on a mis-click.
    const engineToggle = async (next) => {
        await setMerchantEngine(merchantId, next ? 'V2' : 'V1');
        load();
    };
    const shadowToggle = async (next) => {
        await setMerchantShadow(merchantId, next);
        load();
    };

    return (
        <>
            <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs">
                <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>{pending?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={TYPE.rowHelp}>{pending?.body}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)} sx={SX.ctaGhost}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => { pending.commit(); setPending(null); }}
                        sx={SX.cta}
                    >
                        {pending?.confirmLabel || 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {!loading && <StrategyBanner resolved={resolved} />}

            <ConfigSection
                title="PACE"
                subtitle="Whether PACE ranks couriers for this merchant's orders."
                icon={<RocketLaunchOutlinedIcon />}
                state={onV2 ? 'set' : 'inherited'}
                stateHint={onV2 ? 'PACE is live' : 'Allocation rules decide (the default)'}
                defaultOpen
            >
                <ConfigToggle
                    label="PACE live"
                    help="When on, PACE ranks the couriers of every allocation rule whose priority 1 is the PACE entry. When off, those rules book in their own courier order."
                    checked={onV2}
                    onChange={engineToggle}
                    confirm
                    onRequestConfirm={({ next, commit }) => setPending({
                        title: next ? 'Turn PACE live?' : 'Turn PACE off?',
                        body: next
                            ? `PACE will rank the couriers for new orders of ${merchant?.name || 'this merchant'}. Any cheapest or custom courier rules stay saved but stop being used.`
                            : `New orders for ${merchant?.name || 'this merchant'} will book in the allocation rules' own courier order. PACE stops ranking.`,
                        confirmLabel: next ? 'Turn PACE live' : 'Turn PACE off',
                        commit,
                    })}
                    audit={cfg?.paceEnabledBy && onV2 ? { by: cfg.paceEnabledBy, at: fmt(cfg.paceEnabledAt) }
                        : cfg?.paceDisabledBy && !onV2 ? { by: cfg.paceDisabledBy, at: fmt(cfg.paceDisabledAt) }
                        : null}
                />
                <ConfigToggle
                    label="Score PACE in shadow"
                    help="Runs PACE alongside the allocation rules and records its ranking for comparison. It never affects which courier is picked."
                    checked={!!cfg?.paceV2ShadowEnabled}
                    onChange={shadowToggle}
                    disabled={onV2}
                    disabledReason="Not applicable — PACE is already live, so there is nothing to shadow."
                    confirm
                    onRequestConfirm={({ next, commit }) => setPending({
                        title: next ? 'Turn on shadow scoring?' : 'Turn off shadow scoring?',
                        body: next
                            ? 'PACE will score every order for comparison. Allocation itself does not change.'
                            : 'PACE stops scoring orders, so the comparison data will no longer be collected.',
                        confirmLabel: next ? 'Turn on' : 'Turn off',
                        commit,
                    })}
                />
                <ConfigRow
                    label="PACE entry allowed in allocation rules"
                    help="Lets rule uploads use the PACE entry at priority 1. Managed from Masters → Merchants → merchant details."
                    value={cfg?.automatedCourierAllocationEnabled ? 'On' : 'Off'}
                    state={cfg?.automatedCourierAllocationEnabled ? 'set' : 'unset'}
                />
            </ConfigSection>

            {children && (
                <ConfigSection
                    title="Courier rules & history"
                    subtitle="The rule set and the record of what was published when."
                    icon={<RuleOutlinedIcon />}
                    defaultOpen
                >
                    {children}
                </ConfigSection>
            )}
        </>
    );
}

function fmt(v) {
    if (!v) return null;
    try {
        return new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return null; }
}
